<template>
  <FlotoContentLoader :loading="loading">
    <FlotoCrudContainer
      as-table
      :selectable="selectable"
      prepend-new-item
      :fetch-fn="getDiscoveryAgents"
      :delete-fn="deleteDiscoveryAgents"
      :columns="tableColumns"
      v-bind="$attrs"
      @selection-change="$emit('selection-change', $event)"
      @pageChange="bulkUpdateErrors = null"
    >
      <template v-slot:add-controls="{ refreshList }">
        <MRow :gutter="0" class="mb-2">
          <MCol :size="hideSettingBtn ? 12 : 6" class="flex items-center">
            <div v-if="searchable" class="flex-1">
              <FlotoSearchBar
                ref="searchBar"
                :allow-keywords="false"
                :allow-save-search="false"
                :persist-criteria="false"
                :module-name="$constants.AGENT"
                :exclude-search-params="excludedSearchParams"
                @change="applyCriteria($event, refreshList)"
              />
            </div>
            <FlotoDropdownPicker
              v-if="allowBulkAction"
              :options="filterOptions"
              :value="(filterType || {}).value"
              class="mx-2"
              @change="handleFilterChange($event, refreshList)"
            >
              <template v-slot:trigger="{ toggle }">
                <MButton
                  id="all-btn"
                  variant="neutral-lighter"
                  :shadow="false"
                  class="pr-1"
                  @click="toggle"
                >
                  {{ filterType ? $t(filterType) : $t('all') }}

                  <MIcon name="filter" size="lg" />
                </MButton>
              </template>
            </FlotoDropdownPicker>
            <MTooltip v-if="allowBulkAction">
              <template v-slot:trigger>
                <MButton
                  id="refresh-btn"
                  shape="circle"
                  variant="neutral-lighter"
                  :shadow="false"
                  class="mx-1"
                  @click="refreshList"
                >
                  <MIcon name="sync" />
                </MButton>
              </template>
              {{ $tc('refresh') }}
            </MTooltip>
          </MCol>
        </MRow>
      </template>
      <template v-slot:before-list>
        <MRow v-if="bulkUpdateErrors" class="px-4">
          <MCol>
            <MAlert type="error" banner>
              <BulkUpdateErrorDrawer
                :module-name="moduleName"
                :errors="bulkUpdateErrors"
              >
                <b
                  class="text-secondary-red text-underline mr-1"
                  style="text-decoration: underline"
                >
                  {{ bulkUpdateErrors.length }}
                </b>
              </BulkUpdateErrorDrawer>
              {{
                $t('bulk_update_error', {
                  resource: $tc(moduleName, bulkUpdateErrors.length),
                })
              }}
              <template v-slot:close-btn>
                <MIcon
                  class="opacity-75 text-regular"
                  name="times"
                  @click="bulkUpdateErrors = null"
                />
              </template>
            </MAlert>
          </MCol>
        </MRow>
      </template>
      <template v-if="allowBulkAction" v-slot:bulk-actions="slotData">
        <BulkActions
          :filter="filterType"
          v-bind="slotData"
          :type="type"
          @bulk-update-error="handleBulkUpdateErrors"
        />
      </template>
      <template v-slot:name="{ item }">
        <td class="resource-link text-ellipsis">
          <FlotoDot
            :bg="item.active ? '#6bda7b' : '#ffe000'"
            class="cursor-pointer mr-2"
            :size="15"
          />
          {{ item.name }}
        </td>
      </template>
      <template v-slot:pollerId="{ item }">
        <td class="text-ellipsis">
          {{ getPollerName(item.pollerId) }}
        </td>
      </template>
      <template v-slot:lastRefreshCallTime="{ item }">
        <td class="text-ellipsis">
          {{ item.lastRefreshCallTime | datetime }}
        </td>
      </template>
      <template v-slot:usageDuration="{ item }">
        <td class="text-ellipsis">
          {{ item.usageDuration | duration }}
        </td>
      </template>
      <template v-slot:lastUsedTime="{ item }">
        <td class="text-ellipsis">
          <template v-if="item.lastUsedTime">
            {{ item.lastUsedTime | datetime }} ({{
              item.lastUsedTime | timeago
            }})
          </template>
          <template v-else>---</template>
        </td>
      </template>
      <template
        v-if="optForMoreSecureCommunication"
        v-slot:credentialProfileName="{ item }"
      >
        <td class="text-ellipsis">
          <div class="flex">
            {{ item.credentialProfileName || '---' }}
          </div>
        </td>
      </template>
      <template v-slot:architecture="{ item }">
        <td class="text-ellipsis">
          <span v-if="item.architecture">{{ $tc(item.architecture) }}</span>
          <span v-else>---</span>
        </td>
      </template>
      <template v-slot:actions="{ item, remove, refreshList, toggleSelectAll }">
        <td
          :class="{
            'sticky-action': actionSticked.indexOf(item.id) >= 0,
            'hover-action-container': hoverableActions,
          }"
          @click="() => toggleSelectAll()"
        >
          <slot
            name="action"
            :item="item"
            :stick-action="() => stickActionFor(item)"
            :unstick-action="() => unStickActionFor(item)"
          >
            <AgentLogRequestDrawer
              v-if="!item.archived"
              :resource="item"
              :type="type"
              @refresh="refreshList"
            />
            <FlotoDeleteBtn
              v-if="!item.archived"
              :message="
                $t('confirm_archive_item', {
                  item: type ? $t(type) : $t('discovery_agent'),
                })
              "
              @confirm="remove"
            />
            <MTooltip v-if="item.archived">
              <template v-slot:trigger>
                <a class="mx-2" @click="restoreAgents(item.id, refreshList)">
                  <MIcon
                    :id="`restore-${type}-${item.name}`"
                    name="trash-undo-alt"
                    size="lg"
                  />
                </a>
              </template>
              {{ $t('restore') }}
            </MTooltip>
          </slot>
        </td>
      </template>
    </FlotoCrudContainer>
  </FlotoContentLoader>
</template>
<script>
import { discoveryAgentExcludeSearchParams } from '@components/search/sources'
import CloneDeep from 'lodash/cloneDeep'
import { transformSearchCriteriaForServer } from '@data/search'
import {
  transformBatchQualForServer,
  buildRelationalQualificationStructure,
} from '@data/qualification'
import {
  getDiscoveryAgentsApi,
  deleteDiscoveryAgentsApi,
  restoreDiscoveryAgentsApi,
  getDiscoverPollerConfigApi,
  getAgentSettingApi,
} from '../../api/discovery-agents-api'
import BulkActions from './bulk-actions'
import AgentLogRequestDrawer from './discovery-agent/agent-log-request-drawer.vue'
import Uniq from 'lodash/uniq'
import { authComputed } from '@state/modules/auth'

export default {
  name: 'DiscoveryAgentsList',
  components: { BulkActions, AgentLogRequestDrawer },
  props: {
    hideSettingBtn: { type: Boolean, default: false },
    fetchFn: { type: Function, default: undefined },
    deleteFn: { type: Function, default: undefined },
    restoreFn: { type: Function, default: undefined },
    hasActions: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false },
    selectable: { type: Boolean, default: false },
    allowBulkAction: { type: Boolean, default: false },
    hiddenColumns: {
      type: Array,
      default() {
        return ['lastUsedTime', 'usageDuration']
      },
    },
    hoverableActions: { type: Boolean, default: false },
    excludeItemsById: {
      type: Array,
      default() {
        return []
      },
    },
    type: {
      type: String,
      default() {
        return 'agent'
      },
    },
  },
  data() {
    this.filterOptions = [
      { text: this.$tc('all'), key: 'all' },
      { text: `${this.$tc('archived')}`, key: 'archived' },
    ]
    return {
      loading: true,
      filterType: undefined,
      optForMoreSecureCommunication: false,
      searchCriterias: {
        quals: [],
      },
      actionSticked: [],
      pollerList: [],
      bulkUpdateErrors: null,
    }
  },
  computed: {
    ...authComputed,
    excludedSearchParams() {
      if (!this.optForMoreSecureCommunication) {
        return [...discoveryAgentExcludeSearchParams, 'credentialProfileId']
      }
      return discoveryAgentExcludeSearchParams
    },
    tableColumns() {
      return [
        { name: `${this.$tc('id')}`, key: 'name' },
        { name: `${this.$tc('host_name')}`, key: 'hostName' },
        { name: `${this.$tc('ip_address')}`, key: 'ipAddress' },
        { name: `${this.$tc('poller')}`, key: 'pollerId' },
        { name: `${this.$tc('os')}`, key: 'osName' },
        {
          name: `${this.$tc('version')}`,
          key: 'agentVersion',
        },
        ...(this.hiddenColumns.indexOf('lastRefreshCallTime') < 0
          ? [{ name: this.$tc('last_sync_date'), key: 'lastRefreshCallTime' }]
          : []),
        ...(this.hiddenColumns.indexOf('usageDuration') < 0
          ? [{ name: this.$tc('usage_duration'), key: 'usageDuration' }]
          : []),
        ...(this.hiddenColumns.indexOf('lastUsedTime') < 0
          ? [{ name: this.$tc('last_used'), key: 'lastUsedTime' }]
          : []),
        ...(this.optForMoreSecureCommunication
          ? [
              {
                name: `${this.$tc('agent_credential_profile')}`,
                key: 'credentialProfileName',
              },
            ]
          : []),
        ...(this.hiddenColumns.indexOf('architecture') < 0
          ? [{ name: this.$tc('architecture'), key: 'architecture' }]
          : []),
        ...(this.hasActions
          ? [
              {
                name: `${this.$tc('action', 2)}`,
                key: 'actions',
                width: '120px',
              },
            ]
          : []),
      ]
    },
  },

  created() {
    this.getAgentServerCommunicaionPrefereces()
  },
  methods: {
    applyCriteria(searchCriteria, refreshList) {
      this.searchCriterias = searchCriteria
      refreshList()
    },
    getAgentServerCommunicaionPrefereces() {
      return getAgentSettingApi().then((response) => {
        this.optForMoreSecureCommunication =
          response.optForMoreSecureCommunication
        this.loading = false
      })
    },
    getDiscoveryAgents(limit, offset) {
      const criterias = CloneDeep(this.searchCriterias)
      if (this.excludeItemsById.length) {
        const q = transformSearchCriteriaForServer(this.$constants.AGENT, {
          operator: { value: 'not_in' },
          paramName: 'id',
          value: this.excludeItemsById,
          valueType: 'long',
        })
        criterias.quals.push(q)
      }
      const q = transformBatchQualForServer(
        [
          buildRelationalQualificationStructure(
            'os_platform',
            'not_in',
            ['android', 'ios'],
            'listEnum',
            'variable'
          ),
        ],
        'and'
      )
      criterias.quals.push(q)
      if (this.fetchFn) {
        return this.fetchFn(
          criterias,
          this.filterType ? { [this.filterType]: true } : {},
          limit,
          offset
        ).then((data) => {
          const pollerIds = data.items.map((i) => i.pollerId)
          if (pollerIds.length) {
            this.getPollers(pollerIds)
          }
          return data
        })
      }
      return getDiscoveryAgentsApi(
        criterias,
        this.filterType ? { [this.filterType]: true } : {},
        limit,
        offset
      ).then((data) => {
        const pollerIds = data.items.map((i) => i.pollerId)
        if (pollerIds.length) {
          this.getPollers(pollerIds)
        }
        return data
      })
    },
    deleteDiscoveryAgents({ id }) {
      const arr = []
      arr.push(id)
      if (this.deleteFn) {
        return this.deleteFn(arr)
      }
      return deleteDiscoveryAgentsApi(arr)
    },
    handleFilterChange(key, refreshList) {
      this.filterType = key === 'all' ? undefined : key
      refreshList()
    },
    handleBulkUpdateErrors(errors) {
      this.bulkUpdateErrors = errors
    },
    stickActionFor(item) {
      this.actionSticked = Uniq([...this.actionSticked, item.id])
    },
    unStickActionFor(item) {
      this.actionSticked = this.actionSticked.filter((id) => id !== item.id)
    },
    restoreAgents(id, refreshList) {
      const arr = []
      arr.push(id)
      if (this.restoreFn) {
        return this.restoreFn(arr).then(() => {
          refreshList()
        })
      }
      return restoreDiscoveryAgentsApi(arr).then(() => {
        refreshList()
      })
    },
    getPollers(ids) {
      return getDiscoverPollerConfigApi({ ids }).then((data) => {
        this.pollerList = data.items
      })
    },
    getPollerName(id) {
      const data = this.pollerList.find((i) => i.id === id)
      return (data || {}).name ? (data || {}).name : '---'
    },
  },
}
</script>
